import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';

import WebClientFeatureFlagResource from 'flow/api/webclientfeatureflag';
import { getAndSetFeatureFlags } from 'flow/actions/featureflags-actions';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { FeatureFlagObject, FeatureFlagTypes } from 'flow/types/FeatureFlags';

export const FEATURE_FLAGS_QUERY_KEY = ['webClientFeatureFlags'];

const fetchFeatureFlags = (): Promise<FeatureFlagObject[]> =>
    new Promise((resolve, reject) => {
        WebClientFeatureFlagResource.getFeatureFlags({
            onResult: resolve,
            onError: reject,
        });
    });

const putFeatureFlag = (
    feature: FeatureFlagTypes,
    enabled: boolean,
): Promise<FeatureFlagObject[]> =>
    new Promise((resolve, reject) => {
        WebClientFeatureFlagResource.updateFeatureFlag(
            { onResult: resolve, onError: reject },
            feature,
            { enabled },
        );
    });

export const useWebClientFeatureFlags = (enabled = true) =>
    useAuthenticatedQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEY,
        queryFn: fetchFeatureFlags,
        staleTime: 18 * 60 * 60 * 1000,
        enabled,
    });

export const useUpdateFeatureFlag = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            feature,
            enabled,
        }: {
            feature: FeatureFlagTypes;
            enabled: boolean;
        }) => putFeatureFlag(feature, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: FEATURE_FLAGS_QUERY_KEY,
            });
        },
    });
};

// Syncs React Query feature flag data into Redux so existing selectors keep working.
export const useSyncFeatureFlags = (enabled: boolean) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const queryResult = useWebClientFeatureFlags(enabled);

    useEffect(() => {
        if (queryResult.data) {
            dispatch(getAndSetFeatureFlags(queryResult.data));
        }
    }, [dispatch, queryResult.data]);

    // Clear cached feature flags on logout so they're re-fetched on next login.
    useEffect(() => {
        if (!enabled) {
            queryClient.removeQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
        }
    }, [enabled, queryClient]);

    return queryResult;
};
